import { Injectable, inject } from '@angular/core';
import { classes } from 'polytype';

import type {
    DeploymentAdapter,
    DeploymentFigure,
    DeploymentInvocationLogsAdapter,
    DeploymentInvocationStatsAdapter,
} from '@app/modules/management/models/deployment.model';
import { DEPLOYMENT_PATH } from '@app/shared/constants/api-route.config';
import {
    type ItemResponse,
    type ListResponse,
    type StatusResponse,
    WithCrudService,
} from '@app/shared/services/mixins/crud.service';
import { WithLogService } from '@app/shared/services/mixins/log.service';
import { RestService } from '@app/shared/services/rest.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';

@Injectable({
    providedIn: 'root',
})
export class DeploymentService extends classes(WithLogService, WithCrudService<DeploymentAdapter>) {
    protected endpoint = DEPLOYMENT_PATH;

    protected readonly api = inject(RestService);
    protected readonly toast = inject(ToastNotificationService);

    getUiUrl(id: number) {
        return `${this.api.baseUrl()}${this.endpoint}${id}/ui`;
    }

    getLogs(id: number, startDate: string | null, endDate: string | null) {
        return this.api.getWithParams<ItemResponse<DeploymentInvocationLogsAdapter>>(
            `${this.endpoint}${id}/invocation_logs`,
            {
                startDate,
                endDate,
            },
        );
    }

    getStats(id: number, startDate: string | null, endDate: string | null) {
        return this.api.getWithParams<ItemResponse<DeploymentInvocationStatsAdapter>>(
            `${this.endpoint}${id}/stats`,
            {
                startDate,
                endDate,
            },
        );
    }

    getStatsFigures(deploymentId: number, startDate: string | null, endDate: string | null) {
        return this.api.getWithParams<{ result: DeploymentFigure[] }>(
            `${this.endpoint}${deploymentId}/stats/figures`,
            {
                startDate,
                endDate,
            },
        );
    }

    saveStatsFigure(deploymentId: number, figure: Partial<DeploymentFigure>) {
        return this.api.post<ItemResponse<DeploymentFigure>>(
            `${this.endpoint}${deploymentId}/stats/figure`,
            figure,
        );
    }

    reorderStatsFigures(deploymentId: number, figureIds: number[]) {
        return this.api.post<ListResponse<DeploymentFigure>>(
            `${this.endpoint}${deploymentId}/stats/figures/reorder`,
            {
                figureIds,
            },
        );
    }

    runStatsFigure(
        deploymentId: number,
        figureId: number,
        startDate: string | null,
        endDate: string | null,
    ) {
        return this.api.getWithParams<ItemResponse<DeploymentFigure>>(
            `${this.endpoint}${deploymentId}/stats/figure/${figureId}`,
            {
                startDate,
                endDate,
            },
        );
    }

    deleteStatsFigure(deploymentId: number, figureId: number) {
        return this.api.delete<StatusResponse>(
            `${this.endpoint}${deploymentId}/stats/figure/${figureId}`,
        );
    }
}
